import { Box, Divider, MenuItem, MenuList, Typography } from '@mui/material';
import { format } from 'date-fns';
import type { MouseEvent } from 'react';
import { useMemo, useState } from 'react';
import { FORMAT } from '@/shared/constants';
import { useDataFetcher } from '@/shared/hooks';
import { Analytics } from '@/shared/lib';
import type {
  DateRange,
  DateRangeLimits,
  SerializedDateRangeValueObject,
} from '@/shared/types';
import {
  CustomDateRangePreset,
  DateRangePresets,
  fromSerialized,
  getRangeByPreset,
  toSerialized,
} from '@/shared/types';
import { CustomDateRangeSelect } from './ui/CustomDateRangeSelect/CustomDateRangeSelect';
import type { ReasonKeys } from './ui/CustomDateRangeSelect/types';
import { Reason } from './ui/CustomDateRangeSelect/types';
import { Dropdown } from './ui/Dropdown/Dropdown';

const PRESETS_LABEL_MAP: Record<DateRangePresets, string> = {
  [DateRangePresets.ALL_TIME]: 'All time',
  [DateRangePresets.TODAY]: 'Today',
  [DateRangePresets.YESTERDAY]: 'Yesterday',
  [DateRangePresets.LAST_7_DAYS]: 'Last 7 days',
  [DateRangePresets.LAST_14_DAYS]: 'Last 14 days',
  [DateRangePresets.LAST_30_DAYS]: 'Last 30 days',
  [DateRangePresets.LAST_60_DAYS]: 'Last 60 days',
  [DateRangePresets.LAST_90_DAYS]: 'Last 90 days',
};
const ITEMS: { value: DateRangePresets; label: string }[] = [
  { value: DateRangePresets.ALL_TIME, label: 'All time' },
  { value: DateRangePresets.TODAY, label: 'Today' },
  { value: DateRangePresets.YESTERDAY, label: 'Yesterday' },
  { value: DateRangePresets.LAST_7_DAYS, label: 'Last 7 days' },
  { value: DateRangePresets.LAST_14_DAYS, label: 'Last 14 days' },
  { value: DateRangePresets.LAST_30_DAYS, label: 'Last 30 days' },
  { value: DateRangePresets.LAST_60_DAYS, label: 'Last 60 days' },
  { value: DateRangePresets.LAST_90_DAYS, label: 'Last 90 days' },
];

interface Props {
  label?: string;
  dateFormat?: string;
  value: SerializedDateRangeValueObject;
  onChange: (v: SerializedDateRangeValueObject) => void;
  onGetDateRangeLimits?: () => Promise<DateRangeLimits | undefined>;
}

const NO_DATE_RANGE_LIMITS = () => Promise.resolve(undefined);

export function DateRangeSelect({
  label,
  dateFormat,
  value,
  onChange,
  onGetDateRangeLimits,
}: Props) {
  const [open, setOpen] = useState(false);
  const { data: dateRangeLimits } = useDataFetcher(
    onGetDateRangeLimits ?? NO_DATE_RANGE_LIMITS,
  );
  const dateRange: DateRange = useMemo(() => {
    if (value.preset === CustomDateRangePreset) {
      return fromSerialized(value.dateRange);
    } else {
      return getRangeByPreset(value.preset, dateRangeLimits);
    }
  }, [value, dateRangeLimits]);
  const customSelected = value.preset === CustomDateRangePreset;

  const handleOptionSelect = (option: DateRangePresets) => () => {
    setOpen(false);
    onChange({ preset: option });
    Analytics.sendFeatureUsage('date_range_filter', 'change', {
      type: 'preset',
      page: window.location.pathname,
    });
  };
  const handleCustomOptionSelect = (event: MouseEvent) => {
    event.preventDefault();
    onChange({
      preset: CustomDateRangePreset,
      dateRange: toSerialized(
        getRangeByPreset(value.preset as DateRangePresets, dateRangeLimits),
      ),
    });
    Analytics.sendFeatureUsage('date_range_filter', 'select_custom_option', {
      page: window.location.pathname,
    });
  };
  const handleCustomValueChange = (newValue: DateRange, reason: ReasonKeys) => {
    if (reason === Reason.apply) {
      onChange({
        preset: CustomDateRangePreset,
        dateRange: toSerialized(newValue),
      });
    }

    setOpen(false);
  };

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      label={
        <>
          {customSelected
            ? formatRange(dateRange, dateFormat)
            : PRESETS_LABEL_MAP[value.preset]}
          <Box sx={{ position: 'absolute', top: -12, left: 14, px: 0.25 }}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: 2,
                backgroundColor: '#F1F3F8',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                display: 'inline-block',
                position: 'relative',
                fontSize: '0.656rem',
              }}
            >
              {label ?? 'Period'}
            </Typography>
          </Box>
        </>
      }
    >
      <Box sx={{ display: 'flex', width: '100%' }}>
        <MenuList sx={{ width: '100%' }} disablePadding>
          {ITEMS.map((item) => {
            return (
              <MenuItem
                key={item.value}
                selected={!customSelected && item.value === value.preset}
                onClick={handleOptionSelect(item.value)}
              >
                {item.label}
              </MenuItem>
            );
          })}
          <MenuItem
            selected={customSelected}
            onClick={handleCustomOptionSelect}
          >
            Custom dates
          </MenuItem>
        </MenuList>
        {customSelected && (
          <>
            <Divider flexItem orientation="vertical" />
            <CustomDateRangeSelect
              minDate={dateRangeLimits?.min}
              maxDate={dateRangeLimits?.max}
              value={dateRange}
              onChange={handleCustomValueChange}
            />
          </>
        )}
      </Box>
    </Dropdown>
  );
}

const formatRange = (range: DateRange, dateFormat?: string) => {
  return `${format(range.from, dateFormat ?? FORMAT)} - ${format(range.to, dateFormat ?? FORMAT)}`;
};
